import {
  CSSProperties,
  FunctionComponent,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Logger } from '../../../logger/Logger';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { IconButton, Icons } from '../../controls/iconButton/IconButton';

import styles from './Drawer.module.css';

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  onClose?: (event?: MouseEvent<HTMLElement>) => void;
  open: boolean;
  title?: ReactNode;
  className?: string;
  end?: boolean;
  wide?: boolean;
}

export interface DrawerWindowProps extends DrawerProps {
  anchor: { x: number; y: number } | null;
}

const DrawerWindow: FunctionComponent<DrawerWindowProps> = ({
  onClose,
  children,
  title,
  className,
  open,
  end,
  anchor,
  wide,
  ...rest
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const drawerRef = useRef<HTMLDivElement>(null);

  const drawerWithMods = useModifiers(styles, 'drawer', { end, wide }, { withBaseClass: true });

  const handleClose = (event: MouseEvent<HTMLElement> | undefined): void => {
    if (open) {
      onClose?.(event);
    }
  };

  useEffect(() => {
    if (open) {
      drawerRef.current?.classList.remove(styles.drawer_closing);
      setShouldRender(true);
    } else {
      drawerRef.current?.classList.add(styles.drawer_closing);
    }
  }, [open]);

  const onAnimationEndHandle = (): void => {
    if (!open) {
      setShouldRender(false);
    }
  };

  if (!shouldRender) return null;
  const root = document.getElementById('drawerRoot');
  if (!root) {
    Logger.logError('DRAWER', 'info', new Date().getTime(), 'DrawerError', 'Can not find drawer root element');
    return null;
  }
  return createPortal(
    <div
      className={drawerWithMods}
      style={{ '--anchorOffset': anchor ? `translate(${anchor.x}px, ${anchor.y}px)` : '' } as CSSProperties}
      ref={drawerRef}
      onAnimationEnd={onAnimationEndHandle}
    >
      <div className={`${styles.drawer__overlay}`} onClick={handleClose}></div>
      <div data-testid="drawer" className={`${styles.drawer__body} ${className || ''}`} {...rest}>
        <div className={styles.drawer__header}>
          <div className={styles.drawer__headerTitle}>{title}</div>
          <div className={styles.drawer__action}>
            <IconButton icon={Icons.Close} onClick={handleClose} ghost />
          </div>
        </div>
        <div className={styles.drawer__content}>{children}</div>
      </div>
    </div>,
    root
  );
};

export const Drawer: FunctionComponent<DrawerProps> = ({ open, end, ...rest }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const updateAnchor = (): void => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setAnchor({ x: end ? -window.innerWidth + (rect.x + rect.width) : rect.x, y: -window.innerHeight + rect.y });
      }
    };

    const updateAnchorEventCallback = (): void => {
      window.setTimeout(() => {
        updateAnchor();
      });
    };

    updateAnchor();
    window.addEventListener('orientationchange', updateAnchorEventCallback, { passive: true });
    window.addEventListener('resize', updateAnchorEventCallback, { passive: true });

    return () => {
      window.removeEventListener('resize', updateAnchorEventCallback);
      window.removeEventListener('orientationchange', updateAnchorEventCallback);
    };
  }, [end, open]);

  return (
    <>
      <DrawerWindow open={open} end={end} anchor={anchor} {...rest} />
      <div className={styles.drawer__anchor} ref={anchorRef} />
    </>
  );
};
